import React from "react"
import { PILLAR_ENVIRONMENTAL } from "utils/constants/pillars"
import PropTypes from "prop-types"
import ChartSDG13WebsitesGhgMolecule from "views/molecules/canvases/charts/websites/ChartSDG13WebsitesGhgMolecule"
import ChartQuantitativeCo2ImpactsMolecule from "views/molecules/canvases/charts/websites/ChartQuantitativeCo2ImpactsMolecule"
import ChartSdg13MaturityScoresMolecule from "views/molecules/canvases/charts/websites/ChartSDG13MaturityScoresMolecule"
import ChartSDG13WebsiteMaturityKpiMolecule from "views/molecules/canvases/charts/websites/ChartSDG13WebsiteMaturityKpiMolecule"
import ChartSDG13AccessibilityErrorsMolecule from "views/molecules/canvases/charts/websites/ChartSDG13AccessibilityErrorsMolecule"
import ChartSDG13GhgEmissionsPerVisitKPIMolecule from "views/molecules/canvases/charts/websites/ChartSDG13GhgEmissionsPerVisitKPIMolecule"
import { useAppContext } from "components/AppContext"
import ScoreLayoutOrganism from "views/organisms/scores/ScoreLayoutOrganism"
import ChartAnalyzeWebsiteSplitPerItDomain from "views/molecules/canvases/charts/websites/ChartAnalyzeWebsiteSplitPerItDomain"
import { useTranslation } from "react-i18next"
import LegacyMethodologyBanner from "views/molecules/banners/LegacyMethodologyBanner"

const WebsiteAnalyseChartOrganism = ({ score, isOnGlobalDashboard }) => {
  const transSuffix = "analyze:historization.node."
  const websitesScore = score.website_analyse
  const { t } = useTranslation()
  const { score: globalScore } = useAppContext()

  const chart1TrendBadge = globalScore?.trend_badges?.odd13_software_CO2_websites
  const chart2TrendBadge = globalScore?.trend_badges?.accessibility_errors
  const chart5TrendBadge = globalScore?.trend_badges?.odd13_ghg_average_visit_CO2
  const maturityTrendBadge = globalScore?.trend_badges?.website_maturity

  // Legacy-methodology websites keep the previous KPIs, like the per-website dashboard.
  const isLegacy = websitesScore?.legacy_methodology

  // Sub-value under the GHG tile total. With the new methodology the per-visit
  // emission is shown there; legacy keeps its own per-visit tile (chart5), so we
  // leave it empty (undefined hides the sub-value).
  const ghgTilePerVisit = isLegacy
    ? undefined
    : websitesScore?.ghg_average_visit_CO2

  return (
    <>
      {websitesScore?.legacy_methodology && <LegacyMethodologyBanner />}
      <ScoreLayoutOrganism
        forceMaturity
        maturity={websitesScore.pillar_websites_maturity}
        maturityTrendBadge={maturityTrendBadge}
        pillar={PILLAR_ENVIRONMENTAL}
        transSuffix={transSuffix}
        chart1={
          <ChartSDG13WebsitesGhgMolecule
            websiteEmissions={websitesScore?.ghg_emissions_tco2 ?? score.odd13_software_CO2_websites}
            totalEmissions={score.odd13_CO2}
            ghgSingleVisitorEmisison={ghgTilePerVisit}
            hidePercentageOfEmissions={isOnGlobalDashboard}
            trendBadge={chart1TrendBadge}
          />
        }
        chart2={
          isLegacy ? (
            <ChartSDG13AccessibilityErrorsMolecule
              accessibilityErrors={websitesScore.accessibility_errors}
              showAverage
              trendBadge={chart2TrendBadge}
            />
          ) : (
            <ChartSDG13WebsiteMaturityKpiMolecule
              value={score.odd10_websites_accessibility_maturity}
              translationKey="analyze:websites.accessibility_maturity"
            />
          )
        }
        chart3={
          <ChartQuantitativeCo2ImpactsMolecule
            mixedChart={websitesScore.quantitative_co2_impacts}
            title={t(
              "score:score.13.chartGHGWebsitesEmissions.modal.chartQuantitativeCO2Impacts.title"
            )}
          />
        }
        chart4={
          <ChartSdg13MaturityScoresMolecule
            maturityScore={websitesScore.maturity_scores}
          />
        }
        chart5={
          isLegacy ? (
            <ChartSDG13GhgEmissionsPerVisitKPIMolecule
              ghgEmissionsPerVisit={websitesScore?.ghg_average_visit_CO2}
              trendBadge={chart5TrendBadge}
            />
          ) : (
            <ChartSDG13WebsiteMaturityKpiMolecule
              value={score.odd13_software_websites_ecodesign_maturity}
              translationKey="analyze:websites.ecodesign_maturity"
            />
          )
        }
        chart6={
          <ChartAnalyzeWebsiteSplitPerItDomain websiteScore={websitesScore} />
        }
      />
    </>
  )
}

WebsiteAnalyseChartOrganism.propTypes = {
  score: PropTypes.shape(
    {
      odd13_software_CO2_websites: PropTypes.string.isRequired,
      odd13_CO2: PropTypes.string.isRequired,
      website_analyse: PropTypes.object,
    }.isRequired
  ),
  isOnGlobalDashboard: PropTypes.bool,
  trendBadges: PropTypes.object,
}

WebsiteAnalyseChartOrganism.defaultProps = {
  isOnGlobalDashboard: false,
  trendBadges: {},
}

export default WebsiteAnalyseChartOrganism